import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import { env } from 'config/env';

const onbLog = logger.forDomain('ONB');
const tracer = trace.getTracer('@parrot-software/observability');

export type PlacesApiClientType = {
  getPredictions: (input: string, country: string) => Promise<google.maps.places.AutocompletePrediction[]>;
  getDetails: (placeId: string) => Promise<google.maps.places.PlaceResult | null>;
};

let loadPromise: Promise<void> | null = null;

/**
 * Dynamically loads the Google Maps SDK the first time it is needed.
 * Subsequent calls return the same resolved promise.
 */
const loadGoogleMapsScript = (): Promise<void> => {
  if (window.google?.maps?.places) return Promise.resolve();

  if (!loadPromise) {
    loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${env.GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        onbLog.warn('places_sdk_load_failed', {
          'error.kind': 'ScriptLoadError',
          'error.message': 'Failed to load Google Maps SDK',
        });
        reject(new Error('Failed to load Google Maps SDK'));
      };
      document.head.appendChild(script);
    });
  }

  return loadPromise;
};

/**
 * Creates the Places API client with lazy SDK loading.
 * The Google Maps script is loaded on the first call to any method.
 */
export const createPlacesApiClient = (): PlacesApiClientType => {
  let autocompleteService: google.maps.places.AutocompleteService | null = null;
  let placesService: google.maps.places.PlacesService | null = null;

  const getAutocompleteService = () => {
    if (!autocompleteService) {
      autocompleteService = new google.maps.places.AutocompleteService();
    }
    return autocompleteService;
  };

  const getPlacesService = () => {
    if (!placesService) {
      placesService = new google.maps.places.PlacesService(document.createElement('div'));
    }
    return placesService;
  };

  const getPredictions = async (
    input: string,
    country: string,
  ): Promise<google.maps.places.AutocompletePrediction[]> => {
    const span = tracer.startSpan('ONB.places_predictions');
    span.setAttribute('onb.input_length', input.length);
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        await loadGoogleMapsScript();

        return await new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
          getAutocompleteService().getPlacePredictions(
            { input, componentRestrictions: { country } },
            (results, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                onbLog.debug('places_predictions_fetched', {
                  'onb.prediction_count': results.length,
                  'onb.input_length': input.length,
                });
                resolve(results);
              } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                resolve([]);
              } else {
                onbLog.warn('places_predictions_error', {
                  'error.kind': 'PlacesApiError',
                  'onb.places_status': status,
                });
                reject(new Error(`Places API error: ${status}`));
              }
            },
          );
        });
      } finally {
        span.end();
      }
    });
  };

  const getDetails = async (placeId: string): Promise<google.maps.places.PlaceResult | null> => {
    const span = tracer.startSpan('ONB.places_details');
    span.setAttribute('onb.place_id', placeId);
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        await loadGoogleMapsScript();

        return await new Promise<google.maps.places.PlaceResult | null>((resolve) => {
          getPlacesService().getDetails(
            { placeId, fields: ['address_components', 'formatted_address'] },
            (result, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK) {
                resolve(result);
              } else {
                onbLog.warn('places_details_error', {
                  'error.kind': 'PlacesApiError',
                  'onb.place_id': placeId,
                  'onb.places_status': status,
                });
                resolve(null);
              }
            },
          );
        });
      } finally {
        span.end();
      }
    });
  };

  return { getPredictions, getDetails };
};
